/* GASSISA
=========================================================================================
* Descripción: Tabla de soporte para el módulo de Administración de Nóminas
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 07/08/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Permite desplegar nómina completa sin scroll interno
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import { useMemo, useState } from "react";
import { Box, CircularProgress, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const normalizeSearch = (value) =>
    String(value ?? "").toLowerCase().trim();

export default function SizeSoftXTablaNominas({
    columns = [],
    data = [],
    id = "usuario",
    actions = null,
    disablePagination = false,
    rowsPerPageInitial = 8,
    searchPlaceholder = "Buscar registro...",
    emptyMessage = "No hay registros para mostrar",
}) {
    const [busqueda, setBusqueda] = useState("");
    const [ordenCampo, setOrdenCampo] = useState("");
    const [ordenDir, setOrdenDir] = useState("asc");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageInitial);

    const handleCambiarOrden = (campo) => {
        const esAsc = ordenCampo === campo && ordenDir === "asc";
        setOrdenDir(esAsc ? "desc" : "asc");
        setOrdenCampo(campo);
        setPage(0);
    };

    const registrosFiltrados = useMemo(() => {
        const keyword = normalizeSearch(busqueda);
        let rows = (data || []).slice();

        if (keyword) {
            rows = rows.filter((fila) =>
                columns.some((col) =>
                    normalizeSearch(fila?.[col.field]).includes(keyword)
                )
            );
        }

        if (ordenCampo) {
            rows = [...rows].sort((a, b) => {
                const av = a?.[ordenCampo];
                const bv = b?.[ordenCampo];
                if (av == null) return 1;
                if (bv == null) return -1;
                if (typeof av === "number" && typeof bv === "number") {
                    return ordenDir === "asc" ? av - bv : bv - av;
                }
                const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
                return ordenDir === "asc" ? cmp : -cmp;
            });
        }

        return rows;
    }, [busqueda, data, columns, ordenCampo, ordenDir]);

    const filasPagina = !disablePagination
        ? registrosFiltrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : registrosFiltrados;

    const handleCambiarPage = (_, nuevaPage) => setPage(nuevaPage);
    const handleCambiarRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const renderCelda = (fila, col) => {
        if (col.render) return col.render(fila);
        return fila?.[col.field] ?? "";
    };

    const totalColumnas = columns.length + (actions ? 1 : 0);

    return (
        <Paper variant="outlined" sx={{ width: "100%" }}>
            <Box sx={{ p: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <TextField autoComplete="off"
                    size="small"
                    placeholder={searchPlaceholder}
                    value={busqueda}
                    onChange={(e) => {
                        setBusqueda(e.target.value);
                        setPage(0);
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ maxWidth: 340 }}
                />
            </Box>
            <TableContainer sx={disablePagination ? undefined : { maxHeight: "52vh", overflow: "auto" }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.field || col.title}
                                    align={col.align || "left"}
                                    sortDirection={ordenCampo === col.field ? ordenDir : false}
                                    sx={{ whiteSpace: "nowrap", minWidth: col.width }}
                                >
                                    {col.sortable === false ? (
                                        col.title
                                    ) : (
                                        <TableSortLabel
                                            active={ordenCampo === col.field}
                                            direction={ordenCampo === col.field ? ordenDir : "asc"}
                                            onClick={() => handleCambiarOrden(col.field)}
                                        >
                                            {col.title}
                                        </TableSortLabel>
                                    )}
                                </TableCell>
                            ))}
                            {actions && (
                                <TableCell align="center">Acciones</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filasPagina.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={totalColumnas} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">{emptyMessage}</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {filasPagina.map((fila) => (
                            <TableRow hover key={String(fila?.[id] ?? fila?.usuario ?? Math.random())}>
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.field || col.title}
                                        align={col.align || "left"}
                                        sx={{ whiteSpace: "nowrap" }}
                                    >
                                        {renderCelda(fila, col)}
                                    </TableCell>
                                ))}
                                {actions && (
                                    <TableCell align="center">{actions(fila)}</TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {!disablePagination && (
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 8, 10, 25, 100]}
                    count={registrosFiltrados.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    labelRowsPerPage="Registros por pégina"
                    onPageChange={handleCambiarPage}
                    onRowsPerPageChange={handleCambiarRowsPerPage}
                />
            )}
        </Paper>
    );
}